import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useEffect } from "react";
import Home from "@/pages/Home";
import ListView from "@/pages/ListView";
import AuthPage from "@/pages/AuthPage";
import AdminPanel from "@/pages/AdminPanel";
import BusinessPanel from "@/pages/BusinessPanel";
import FriendVibesPage from "@/pages/FriendVibesPage";
import OnboardingPage from "@/pages/OnboardingPage";
import NotFound from "@/pages/not-found";
import ErrorBoundary from "@/components/ErrorBoundary";
import { initPostHog } from "@/lib/posthog";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();

  const publicPaths = ["/auth", "/onboarding"];
  const isPublicPage = publicPaths.some(p => location === p || location.startsWith(p + "?"));

  if (isLoading) {
    if (isPublicPage) return <>{children}</>;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!user && !isPublicPage) {
    return <Redirect to="/auth" />;
  }

  if (user && user.onboardingComplete === false && location !== "/onboarding" && !isPublicPage) {
    return <Redirect to="/onboarding" />;
  }

  return <>{children}</>;
}

function RootRoute() {
  const { user } = useAuth();
  if (user?.role === "business") {
    return <Redirect to="/business" />;
  }
  return <Home />;
}

// When a push notification is tapped while the app is already open, the service
// worker (client/public/sw.js) focuses this client and posts a NOTIFICATION_CLICK
// message carrying the notification's `url`. Route it through wouter so Home's
// deep-link effect (parses ?venue / ?profile / ?media) opens the right screen.
// The fresh-launch case is handled by the SW's openWindow(url) directly.
function NotificationDeepLinkListener() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (data?.type === "NOTIFICATION_CLICK" && typeof data.url === "string") {
        try {
          const u = new URL(data.url, window.location.origin);
          setLocation(u.pathname + u.search);
        } catch {
          setLocation(data.url);
        }
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [setLocation]);
  return null;
}

function Router() {
  return (
    <AuthGuard>
      <Switch>
        <Route path="/">{() => <RootRoute />}</Route>
        <Route path="/map" component={Home} />
        <Route path="/list" component={ListView} />
        <Route path="/auth" component={AuthPage} />
        <Route path="/admin" component={AdminPanel} />
        <Route path="/business" component={BusinessPanel} />
        <Route path="/friends/:friendId/vibes" component={FriendVibesPage} />
        <Route path="/onboarding" component={OnboardingPage} />
        <Route component={NotFound} />
      </Switch>
    </AuthGuard>
  );
}

function App() {
  useEffect(() => {
    initPostHog();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <NotificationDeepLinkListener />
          <Router />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
